import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from "react";

/* The voyage: seven waypoints, each stamps the captain's log once. */
export const STATIONS = [
  { id: "boat", name: "The Boatyard" },
  { id: "crew", name: "The Muster Dock" },
  { id: "water", name: "Open Water" },
  { id: "notes", name: "Driftwood Strait" },
  { id: "table", name: "Table Island" },
  { id: "wall", name: "The Notice Wall" },
  { id: "asks", name: "One Ask Buoy" },
] as const;

export type StationId = (typeof STATIONS)[number]["id"];

export const MAX_CREW = 4;

type GameState = {
  started: boolean;
  boatId: string | null;
  crew: string[];
  stamps: StationId[];
};

type GameAction =
  | { type: "start" }
  | { type: "pickBoat"; id: string }
  | { type: "addCrew"; name: string }
  | { type: "removeCrew"; name: string }
  | { type: "stamp"; id: StationId }
  | { type: "reset" };

const initialState: GameState = {
  started: false,
  boatId: null,
  crew: [],
  stamps: [],
};

const STORAGE_KEY = "same-boat-voyage";

function loadState(): GameState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw) as Partial<GameState>;
    return {
      started: Boolean(parsed.started),
      boatId: typeof parsed.boatId === "string" ? parsed.boatId : null,
      crew: Array.isArray(parsed.crew)
        ? parsed.crew.filter((c): c is string => typeof c === "string").slice(0, MAX_CREW)
        : [],
      stamps: Array.isArray(parsed.stamps)
        ? (parsed.stamps.filter((s): s is StationId =>
            STATIONS.some((st) => st.id === s),
          ) as StationId[])
        : [],
    };
  } catch {
    return initialState;
  }
}

function reducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "start":
      return state.started ? state : { ...state, started: true };
    case "pickBoat":
      return { ...state, boatId: action.id, started: true };
    case "addCrew": {
      const name = action.name.trim().slice(0, 24);
      if (!name || state.crew.length >= MAX_CREW || state.crew.includes(name)) return state;
      return { ...state, crew: [...state.crew, name] };
    }
    case "removeCrew":
      return { ...state, crew: state.crew.filter((c) => c !== action.name) };
    case "stamp":
      if (state.stamps.includes(action.id)) return state;
      return { ...state, started: true, stamps: [...state.stamps, action.id] };
    case "reset":
      return initialState;
  }
}

type GameContextValue = GameState & {
  start: () => void;
  pickBoat: (id: string) => void;
  addCrew: (name: string) => void;
  removeCrew: (name: string) => void;
  stamp: (id: StationId) => void;
  reset: () => void;
  hasStamp: (id: StationId) => boolean;
  stampCount: number;
};

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* private mode etc.; the voyage just won't persist */
    }
  }, [state]);

  const value = useMemo<GameContextValue>(
    () => ({
      ...state,
      start: () => dispatch({ type: "start" }),
      pickBoat: (id) => dispatch({ type: "pickBoat", id }),
      addCrew: (name) => dispatch({ type: "addCrew", name }),
      removeCrew: (name) => dispatch({ type: "removeCrew", name }),
      stamp: (id) => dispatch({ type: "stamp", id }),
      reset: () => dispatch({ type: "reset" }),
      hasStamp: (id) => state.stamps.includes(id),
      stampCount: state.stamps.length,
    }),
    [state],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside GameProvider");
  return ctx;
}
